import { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { fetchCurrentUser } from '@/store/slices/authSlice';
import { MainLayout } from '@/components/layout';
import { LoadingOverlay } from '@/components/common';
import { TourProvider, Tour, LoginNotification } from '@/components/tour';
import { InstallPrompt } from '@/components/common/InstallPrompt';

// Lazy-loaded pages — each becomes a separate JS chunk
const Landing = lazy(() => import('@/pages/Landing').then(m => ({ default: m.Landing })));
const Auth = lazy(() => import('@/pages/Auth').then(m => ({ default: m.Auth })));
const AuthCallback = lazy(() => import('@/pages/AuthCallback').then(m => ({ default: m.AuthCallback })));
const Planner = lazy(() => import('@/pages/Planner').then(m => ({ default: m.Planner })));
const Tracks = lazy(() => import('@/pages/Tracks').then(m => ({ default: m.Tracks })));
const Notes = lazy(() => import('@/pages/Notes').then(m => ({ default: m.Notes })));
const NoteEditor = lazy(() => import('@/pages/Notes').then(m => ({ default: m.NoteEditor })));
const Habits = lazy(() => import('@/pages/Habits').then(m => ({ default: m.Habits })));
const BlogList = lazy(() => import('@/pages/BlogList').then(m => ({ default: m.BlogList })));
const BlogPost = lazy(() => import('@/pages/BlogPost').then(m => ({ default: m.BlogPost })));
const BlogWrite = lazy(() => import('@/pages/BlogWrite').then(m => ({ default: m.BlogWrite })));
const PublicBlogPost = lazy(() => import('@/pages/PublicBlogPost').then(m => ({ default: m.PublicBlogPost })));
const Settings = lazy(() => import('@/pages/Settings').then(m => ({ default: m.Settings })));

const ROUTE_TITLES: Record<string, string> = {
  '/planner': 'Planner | DayDrive',
  '/tracks': 'Tracks | DayDrive',
  '/habits': 'Habits | DayDrive',
  '/notes': 'Notes | DayDrive',
  '/blog': 'Blog | DayDrive',
  '/settings': 'Settings | DayDrive',
};

function RouteTitle() {
  const { pathname } = useLocation();
  const base = '/' + pathname.split('/')[1];
  const title = ROUTE_TITLES[base] || 'DayDrive';
  return <Helmet><title>{title}</title></Helmet>;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAppSelector((state) => state.auth);

  if (isLoading) {
    return <LoadingOverlay message="Loading..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <TourProvider>
      <RouteTitle />
      {children}
      <Tour />
      <LoginNotification />
      <InstallPrompt />
    </TourProvider>
  );
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const dispatch = useAppDispatch();
  const { token, isLoading } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (token) {
      dispatch(fetchCurrentUser());
    }
  }, [dispatch, token]);

  if (isLoading && token) {
    return <LoadingOverlay message="Loading your workspace..." />;
  }

  return (
    <Suspense fallback={<LoadingOverlay message="Loading..." />}>
      <Routes>
        {/* Landing page */}
        <Route path="/landing" element={<Landing />} />

        {/* Public routes - Auth page handles both login and signup */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Auth />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Auth />
            </PublicRoute>
          }
        />

        {/* OAuth callback */}
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Protected routes */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/planner" replace />} />
          <Route path="planner" element={<Planner />} />
          <Route path="planner/:id" element={<Planner />} />
          {/* Keep old tasks route for backwards compatibility */}
          <Route path="tasks" element={<Navigate to="/planner" replace />} />
          <Route path="tracks" element={<Tracks />} />
          <Route path="tracks/:trackId" element={<Tracks />} />
          <Route path="habits" element={<Habits />} />
          <Route path="notes" element={<Notes />} />
          <Route path="notes/:id" element={<NoteEditor />} />

          {/* Blog routes */}
          <Route path="blog" element={<BlogList />} />
          <Route path="blog/new" element={<BlogWrite />} />
          <Route path="blog/public/:id" element={<PublicBlogPost />} />
          <Route path="blog/:slug" element={<BlogPost />} />
          <Route path="blog/:id/edit" element={<BlogWrite />} />

          {/* Settings */}
          <Route path="settings" element={<Settings />} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
